import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Check, Leaf } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import { Button } from '@/components/ui/button';

const ProductCard = ({ product, showDiscount = false }) => {
  const { t, getLocalizedField } = useLanguage();
  const { addToCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [isAdding, setIsAdding] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAdding(true);
    await addToCart(product, 1);
    setTimeout(() => setIsAdding(false), 1500);
  };

  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await toggleFavorite(product.id);
  };

  const isProductFavorite = isFavorite(product.id);

  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className="group relative"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="card-glass p-4 h-full">
          {/* Image Container */}
          <div className="relative aspect-square rounded-2xl overflow-hidden bg-slate-100 mb-4">
            {/* Loading skeleton */}
            {!imageLoaded && (
              <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100" />
            )}
            
            {/* Product Image */}
            <motion.img
              src={product.image}
              alt={getLocalizedField(product, 'name')}
              className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              loading="lazy"
            />

            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-2">
              {(showDiscount || product.is_on_sale) && discount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="px-2 py-1 rounded-full bg-secondary text-white text-xs font-bold"
                >
                  -{discount}% {t('common.off')}
                </motion.span>
              )}
              {product.is_eco && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="px-2 py-1 rounded-full bg-primary text-white text-xs font-bold flex items-center gap-1"
                >
                  <Leaf className="w-3 h-3" />
                  Eco
                </motion.span>
              )}
            </div>

            {/* Favorite Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleToggleFavorite}
              className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                isProductFavorite
                  ? 'bg-red-500 text-white'
                  : 'bg-white/80 backdrop-blur-sm text-slate-600 hover:bg-white'
              }`}
              data-testid={`favorite-btn-${product.id}`}
            >
              <Heart className={`w-4 h-4 ${isProductFavorite ? 'fill-current' : ''}`} />
            </motion.button>

            {/* Quick Add Button - Shows on hover */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileHover={{ opacity: 1, y: 0 }}
              className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Button
                onClick={handleAddToCart}
                disabled={isAdding}
                className={`w-full rounded-xl transition-all ${
                  isAdding
                    ? 'bg-green-500 hover:bg-green-500'
                    : 'btn-primary'
                }`}
                data-testid={`add-to-cart-btn-${product.id}`}
              >
                <AnimatePresence mode="wait">
                  {isAdding ? (
                    <motion.span
                      key="added"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      {t('products.addedToCart')}
                    </motion.span>
                  ) : (
                    <motion.span
                      key="add"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      className="flex items-center gap-2"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      {t('products.addToCart')}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Button>
            </motion.div>
          </div>

          {/* Product Info */}
          <div className="space-y-2">
            {/* Brand */}
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              {product.brand}
            </p>

            {/* Name */}
            <h3 className="font-semibold text-slate-900 line-clamp-2 group-hover:text-primary transition-colors">
              {getLocalizedField(product, 'name')}
            </h3>

            {/* Rating */}
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="text-sm font-medium text-slate-700">{product.rating}</span>
              <span className="text-xs text-slate-400">({product.reviews_count})</span>
            </div>

            {/* Price */}
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xl font-bold text-primary">
                ${product.price.toFixed(2)}
              </span>
              {product.original_price && (
                <span className="text-sm text-slate-400 line-through">
                  ${product.original_price.toFixed(2)}
                </span>
              )}
            </div>

            {/* Unit */}
            <p className="text-xs text-slate-400">{product.unit}</p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
